import {
	type Action,
	type BoundActions,
	createHook,
	createStore,
	type HookFunction,
} from 'react-sweet-state';

export interface SuggestionCycle {
	setsShown: number;
	selected: boolean;
	totalSuggestionsShown: number;
}

export interface AutoSuggestTrackingState {
	suggestionsCyclesShownCount: number;
	suggestionSelectionCount: number;
	suggestionSetsBeforeSelectionCount: number;
	totalSuggestionsShownCount: number;
	suggestionCycles: SuggestionCycle[];
	selectedTexts: string[];
}

const initialState: AutoSuggestTrackingState = {
	suggestionsCyclesShownCount: 0,
	suggestionSelectionCount: 0,
	suggestionSetsBeforeSelectionCount: 0,
	totalSuggestionsShownCount: 0,
	suggestionCycles: [],
	selectedTexts: [],
};

const actions = {
	incrementSuggestionSetsShown:
		(): Action<AutoSuggestTrackingState> =>
		({ setState, getState }) => {
			const currentState = getState();
			setState({
				suggestionsCyclesShownCount: currentState.suggestionsCyclesShownCount + 1,
			});
		},

	incrementSuggestionSelected:
		(): Action<AutoSuggestTrackingState> =>
		({ setState, getState }) => {
			const currentState = getState();
			setState({
				suggestionSelectionCount: currentState.suggestionSelectionCount + 1,
			});
		},

	incrementSuggestionSetsBeforeSelection:
		(totalSuggestionsShownCount: number = 0): Action<AutoSuggestTrackingState> =>
		({ setState, getState }) => {
			const currentState = getState();

			// Check if there's an active cycle (last cycle has selected: false)
			const lastCycle = currentState.suggestionCycles[currentState.suggestionCycles.length - 1];
			const hasActiveCycle = lastCycle && !lastCycle.selected;

			let newCycles;
			if (hasActiveCycle) {
				// Update existing active cycle
				newCycles = [
					...currentState.suggestionCycles.slice(0, -1),
					{
						...lastCycle,
						setsShown: lastCycle.setsShown + 1,
						totalSuggestionsShown: lastCycle.totalSuggestionsShown + totalSuggestionsShownCount,
					},
				];
			} else {
				// Create new cycle
				newCycles = [
					...currentState.suggestionCycles,
					{ setsShown: 1, selected: false, totalSuggestionsShown: totalSuggestionsShownCount },
				];
			}

			setState({
				suggestionSetsBeforeSelectionCount: currentState.suggestionSetsBeforeSelectionCount + 1,
				totalSuggestionsShownCount:
					currentState.totalSuggestionsShownCount + totalSuggestionsShownCount,
				suggestionCycles: newCycles,
			});
		},

	resetSuggestionSetsBeforeSelection:
		(): Action<AutoSuggestTrackingState> =>
		({ setState }) => {
			// Cycles are already stored, just reset the counter
			setState({
				suggestionSetsBeforeSelectionCount: 0,
			});
		},

	completeCurrentCycleWithSelection:
		(selectedText: string): Action<AutoSuggestTrackingState> =>
		({ setState, getState }) => {
			const currentState = getState();

			// Find the last active cycle and mark it as selected
			const lastCycle = currentState.suggestionCycles[currentState.suggestionCycles.length - 1];
			if (lastCycle && !lastCycle.selected) {
				const updatedCycles = [
					...currentState.suggestionCycles.slice(0, -1),
					{ ...lastCycle, selected: true },
				];
				setState({
					suggestionCycles: updatedCycles,
					suggestionSetsBeforeSelectionCount: 0,
					// Store selected text separately for edit distance calculation
					selectedTexts: [...currentState.selectedTexts, selectedText],
				});
			}
		},

	resetAutoSuggestTracking:
		(): Action<AutoSuggestTrackingState> =>
		({ setState }) => {
			setState(initialState);
		},
};

const autoSuggestTrackingStore = createStore({
	initialState,
	actions,
	name: 'autoSuggestTracking',
});

export const useAutoSuggestTracking: HookFunction<
	AutoSuggestTrackingState,
	BoundActions<
		AutoSuggestTrackingState,
		{
			incrementSuggestionSetsShown: () => Action<AutoSuggestTrackingState>;
			incrementSuggestionSelected: () => Action<AutoSuggestTrackingState>;
			incrementSuggestionSetsBeforeSelection: (
				totalSuggestionsShownCount?: number,
			) => Action<AutoSuggestTrackingState>;
			resetSuggestionSetsBeforeSelection: () => Action<AutoSuggestTrackingState>;
			completeCurrentCycleWithSelection: (selectedText: string) => Action<AutoSuggestTrackingState>;
			resetAutoSuggestTracking: () => Action<AutoSuggestTrackingState>;
		}
	>,
	void
> = createHook(autoSuggestTrackingStore);
